import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import FormControl from "@material-ui/core/FormControl";
import IconButton from "@material-ui/core/IconButton";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { formatDateOnly } from "../helpers";
import {
  ACCEPTED_ATTACHMENT_TYPES,
  AttachmentIcon,
  MAX_FILES,
  MAX_THIRD_PARTY_CHARS,
  MAX_TOTAL_BYTES,
  namePastedFiles,
  formatFileSize,
  makeLocalFileId,
  validateAttachmentSelection,
} from "./helpers";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  dropzone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(2),
    textAlign: "center",
    cursor: "pointer",
    outline: "none",
    transition: "border-color 0.2s, background-color 0.2s",
  },
  dropzoneActive: {
    borderColor: "#2E7D32",
    backgroundColor: "rgba(46, 125, 50, 0.08)",
  },
  dropzoneIcon: {
    fontSize: 32,
    color: theme.palette.text.secondary,
    opacity: 0.7,
  },
  fileList: {
    marginTop: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
  },
  fileName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileSize: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    whiteSpace: "nowrap",
  },
  rejected: {
    marginTop: theme.spacing(1),
    fontSize: 12,
    color: "#C62828",
  },
}));

/**
 * Modal de registro de nova ocorrência. Título obrigatório, descrição
 * opcional (aceita colar imagem com Ctrl+V, mesmo padrão de
 * MessageInput.handleInputPaste / ChatMessages.handlePaste), anexos por
 * dropzone. Ao salvar, monta FormData e faz POST multipart em /ocorrencias.
 *
 * `cpfCnpj` já vem normalizado do painel de detalhe da Carteira (campo
 * `documento`) — o backend normaliza de novo antes de gravar, então não há
 * risco de formato aqui.
 *
 * `policies` é a lista de apólices VIGENTES do cliente (`detail.vigentes` do
 * ClientDetailPanel — sem endpoint novo). Regra de seleção:
 * - 0 apólices vigentes: nenhum Select, `policyId` não é enviado.
 * - 1 apólice vigente: nenhum Select, mas o `id` é enviado explicitamente.
 *   Poderíamos deixar o backend vincular sozinho (ele resolve o caso de uma
 *   única vigente sem erro), mas enviar explícito remove qualquer ambiguidade
 *   e mantém o comportamento igual ao caso de 2+, onde o valor sempre vem do
 *   estado local — um único caminho de código, não dois.
 * - 2+ apólices vigentes: Select obrigatório dentro do próprio modal (sem
 *   segundo passo), mostrando produto + seguradora + vigência por opção.
 *   Enquanto não houver seleção, "Salvar" fica desabilitado.
 */
const NewOccurrenceModal = ({ open, cpfCnpj, policies, onClose, onCreated }) => {
  const classes = useStyles();
  const policyOptions = policies || [];
  const requiresPolicyChoice = policyOptions.length > 1;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [thirdPartyInfo, setThirdPartyInfo] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [rejected, setRejected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [policyId, setPolicyId] = useState(policyOptions.length === 1 ? policyOptions[0].id : "");

  // Recalcula o valor default sempre que o modal reabre para um cliente
  // diferente (lista de vigentes pode ter mudado desde a última abertura).
  useEffect(() => {
    if (open) {
      setPolicyId(policyOptions.length === 1 ? policyOptions[0].id : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetState = () => {
    setTitle("");
    setDescription("");
    setThirdPartyInfo("");
    updateAttachments([]);
    setRejected([]);
    setSaving(false);
    setPolicyId(policyOptions.length === 1 ? policyOptions[0].id : "");
  };

  const handleClose = () => {
    if (saving) return;
    resetState();
    onClose();
  };

  // Espelho síncrono da lista, para validar com a soma real mesmo em colagens
  // seguidas (mesmo padrão do FollowUpForm). Soma ≤ 24 MB, mesma mensagem.
  const attachmentsRef = useRef([]);
  const updateAttachments = (next) => {
    attachmentsRef.current = next;
    setAttachments(next);
  };

  const addFiles = useCallback((incoming) => {
    const current = attachmentsRef.current;
    const bytes = current.reduce((sum, entry) => sum + entry.file.size, 0);
    const { accepted, rejected: invalid } = validateAttachmentSelection(
      incoming,
      current.length,
      bytes,
      MAX_TOTAL_BYTES
    );
    setRejected(invalid);
    if (!accepted.length) return;
    const withIds = accepted.map((file) => ({ id: makeLocalFileId(), file }));
    attachmentsRef.current = [...current, ...withIds];
    setAttachments(attachmentsRef.current);
  }, []);

  const handleDrop = useCallback(
    (accepted) => {
      addFiles(accepted);
    },
    [addFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: ACCEPTED_ATTACHMENT_TYPES,
    multiple: true,
    disabled: saving,
  });

  /**
   * Colar imagem na descrição adiciona a imagem colada à lista de anexos a
   * enviar — não escreve nada no texto (mesma decisão de
   * ChatMessages.handlePaste: e.preventDefault() só quando há arquivo).
   */
  const handleDescriptionPaste = (event) => {
    if (event.clipboardData?.files?.length > 0) {
      event.preventDefault();
      addFiles(namePastedFiles(Array.from(event.clipboardData.files)));
    }
  };

  const removeAttachment = (id) => {
    updateAttachments(attachmentsRef.current.filter((entry) => entry.id !== id));
    setRejected([]);
  };

  const canSave = Boolean(title.trim()) && !saving && (!requiresPolicyChoice || Boolean(policyId));

  const handleSave = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;
    if (requiresPolicyChoice && !policyId) return;

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("cpfCnpj", cpfCnpj);
      formData.append("title", trimmedTitle);
      if (description.trim()) formData.append("description", description.trim());
      if (thirdPartyInfo.trim()) formData.append("thirdPartyInfo", thirdPartyInfo.trim());
      if (policyId) formData.append("policyId", policyId);
      attachments.forEach((entry) => formData.append("files", entry.file));

      const { data } = await api.post("/ocorrencias", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      resetState();
      onCreated(data);
    } catch (err) {
      toastError(err);
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Registrar ocorrência</DialogTitle>
      <DialogContent>
        {requiresPolicyChoice ? (
          <FormControl variant="outlined" size="small" fullWidth className={classes.field}>
            <InputLabel id="occurrence-policy-label">Apólice relacionada</InputLabel>
            <Select
              labelId="occurrence-policy-label"
              label="Apólice relacionada"
              value={policyId}
              disabled={saving}
              onChange={(event) => setPolicyId(event.target.value)}
            >
              {policyOptions.map((policy) => (
                <MenuItem key={policy.id} value={policy.id}>
                  {policy.product || "—"} · {policy.insurer || "—"} · vigência até{" "}
                  {formatDateOnly(policy.endDate)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <TextField
          className={classes.field}
          label="Título"
          required
          fullWidth
          variant="outlined"
          size="small"
          value={title}
          disabled={saving}
          onChange={(event) => setTitle(event.target.value)}
        />

        <TextField
          className={classes.field}
          label="Descrição"
          placeholder="Detalhe a ocorrência. Você pode colar uma imagem (Ctrl+V)."
          fullWidth
          multiline
          minRows={4}
          variant="outlined"
          size="small"
          value={description}
          disabled={saving}
          onChange={(event) => setDescription(event.target.value)}
          onPaste={handleDescriptionPaste}
        />

        <TextField
          className={classes.field}
          label="Terceiro envolvido (nome e telefone)"
          fullWidth
          variant="outlined"
          size="small"
          value={thirdPartyInfo}
          disabled={saving}
          inputProps={{ maxLength: MAX_THIRD_PARTY_CHARS }}
          onChange={(event) => setThirdPartyInfo(event.target.value)}
        />

        <Paper
          variant="outlined"
          {...getRootProps()}
          className={`${classes.dropzone} ${isDragActive ? classes.dropzoneActive : ""}`}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon className={classes.dropzoneIcon} />
          <Typography variant="body2">
            {isDragActive ? "Solte os arquivos aqui" : "Arraste arquivos ou clique para selecionar"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            PDF, DOC, DOCX ou imagem — até {MAX_FILES} arquivos, 25 MB cada
          </Typography>
        </Paper>

        {rejected.length > 0 ? (
          <div className={classes.rejected}>
            {rejected.map((item) => (
              <div key={item.name}>
                {item.name} — {item.reason}
              </div>
            ))}
          </div>
        ) : null}

        {attachments.length > 0 ? (
          <div className={classes.fileList}>
            {attachments.map((entry) => (
              <Paper key={entry.id} variant="outlined" className={classes.fileRow}>
                <AttachmentIcon mimeType={entry.file.type} fontSize="small" color="action" />
                <Typography variant="body2" className={classes.fileName} title={entry.file.name}>
                  {entry.file.name}
                </Typography>
                <span className={classes.fileSize}>{formatFileSize(entry.file.size)}</span>
                <IconButton size="small" disabled={saving} onClick={() => removeAttachment(entry.id)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))}
          </div>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" color="primary" disabled={!canSave} onClick={handleSave}>
          {saving ? <CircularProgress size={18} color="inherit" /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewOccurrenceModal;
